/**
 * Settings page retry logic and error categorization helpers.
 *
 * Extracted from useSettingsData.js to reduce file size.
 */

import {
  CIRCUIT_BREAKER_MESSAGES,
  isCircuitBreakerError,
} from "../utils/circuitBreaker";
import { RETRY } from "../config/constants";
import { getErrorMessage } from "../utils/formatters";

/**
 * All possible values returned by categorizeError().
 * Import these constants wherever the category string is compared.
 * In TypeScript this would enforce exhaustiveness at every switch/if site;
 * in JavaScript it prevents magic strings and makes the full set of
 * categories discoverable in one place. The test in useSettingsRetry.test.js
 * verifies that every value categorizeError() returns is present here.
 */
export const ERROR_CATEGORIES = {
  CIRCUIT_BREAKER: "circuit_breaker",
  OFFLINE: "offline",
  NETWORK: "network",
  SERVER: "server",
  UNKNOWN: "unknown",
};

export function getBackoffDelay(attempt) {
  const exponentialDelay = RETRY.BASE_DELAY_MS * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay;
  return Math.min(exponentialDelay + jitter, RETRY.MAX_DELAY_MS);
}

/**
 * Categorize a caught error into one of ERROR_CATEGORIES.
 *
 * @param {Error} err
 * @param {() => boolean} [getOnlineStatus] - Injectable override for navigator.onLine.
 *   Defaults to `() => navigator.onLine`.  Pass a stub in tests to avoid
 *   coupling test output to the browser global.
 * @returns {string} One of the ERROR_CATEGORIES values
 */
export function categorizeError(err, getOnlineStatus = () => navigator.onLine) {
  if (isCircuitBreakerError(err)) return ERROR_CATEGORIES.CIRCUIT_BREAKER;
  if (!getOnlineStatus()) return ERROR_CATEGORIES.OFFLINE;
  if (err?.message === "Network Error" || err?.code === "ERR_NETWORK")
    return ERROR_CATEGORIES.NETWORK;
  if (err?.response?.status >= 500) return ERROR_CATEGORIES.SERVER;
  return ERROR_CATEGORIES.UNKNOWN;
}

// Single config table: each category's heading + message live together.
// All categories including UNKNOWN are present so the complete dispatch
// table is visible in one place. UNKNOWN uses null for message, signalling
// that getDetailedErrorMessage() should fall back to getErrorMessage(err, default).
// When adding a new category:
//   1. Add it to ERROR_CATEGORIES above.
//   2. Add an entry here (heading + message, or message: null for dynamic fallback).
//   3. The test in useSettingsRetry.test.js will fail if a non-UNKNOWN
//      category is missing, making the omission visible immediately.
const ERROR_CATEGORY_CONFIG = {
  [ERROR_CATEGORIES.CIRCUIT_BREAKER]: {
    heading: "Service Recovering",
    message: CIRCUIT_BREAKER_MESSAGES.SERVICE_UNAVAILABLE,
  },
  [ERROR_CATEGORIES.OFFLINE]: {
    heading: "You're Offline",
    message: CIRCUIT_BREAKER_MESSAGES.OFFLINE,
  },
  [ERROR_CATEGORIES.NETWORK]: {
    heading: "Connection Problem",
    message: CIRCUIT_BREAKER_MESSAGES.NETWORK_ERROR,
  },
  [ERROR_CATEGORIES.SERVER]: {
    heading: "Server Error",
    message:
      "Server error occurred. Our team has been notified. Please try again later.",
  },
  // UNKNOWN: message is null so getDetailedErrorMessage falls back to
  // getErrorMessage(err, defaultMessage) for a dynamic, error-specific string.
  // heading is provided so callers that need a heading always find one in this table.
  [ERROR_CATEGORIES.UNKNOWN]: {
    heading: "Unable to Load Settings",
    message: null,
  },
};

// Derived views — callers get the same shape as before; internals are one table.
// Both exported so callers can use either dimension directly without going
// through getDetailedErrorMessage() when they only need headings or messages.
export const ERROR_CATEGORY_HEADINGS = Object.fromEntries(
  Object.entries(ERROR_CATEGORY_CONFIG).map(([k, v]) => [k, v.heading]),
);

export const ERROR_CATEGORY_MESSAGES = Object.fromEntries(
  Object.entries(ERROR_CATEGORY_CONFIG).map(([k, v]) => [k, v.message]),
);

export function getDetailedErrorMessage(err, defaultMessage) {
  const category = categorizeError(err);
  return (
    ERROR_CATEGORY_MESSAGES[category] ?? getErrorMessage(err, defaultMessage)
  );
}
